<script setup lang="ts">
import { useAppIndexStore } from '@base/stores/indexStore'
import type * as tp from '@base/utils/types'
import { useToast } from '#ui/composables/useToast'
import { ENotificationType } from '@base/utils/constants'

const indexStore = useAppIndexStore()
const toast = useToast()

// This component renders NOTHING. <UApp> already brings the toaster along; all there is to do is
// carry store entries over to it.
// It used to do that inside a computed — which deleted from the state it was reading — and a hidden
// div existed for the sole purpose of making that computed run at all. Every message was also
// appended to a bold block that was never cleared, so the page slowly collected every failure of
// the session, rendered with v-html although the text carries server answers.
watch(() => indexStore.state.notifications.length, () => {
  // Copied first: the loop deletes from the very array it walks.
  const pending: tp.INotification[] = [...indexStore.state.notifications]
  for (const notification of pending) {
    const isWarning: boolean = notification.type === ENotificationType.nt_warning
    toast.add({
      // The notification's own id (pfinderAPI: the request url), so the toaster merges a failure that
      // repeats into ONE toast instead of stacking a new one per attempt — measured 14.10.2026: a
      // price-list auto-save retrying against a stopped server piled up a toast every ~3 s.
      id: notification.id,
      title: notification.title,
      description: notification.message,
      color: isWarning ? 'error' : 'primary',
      // An error waits to be dismissed. Whatever disappears by itself cannot be read out, copied or
      // passed on — and this channel exists precisely so that whoever sees it can report it. Only
      // what merely informs may time out.
      duration: isWarning ? 0 : 15000,
    })
    indexStore.deleteNotification(notification.id)
  }
}, { immediate: true })
</script>

<template>
  <!--
    Nothing is shown here: the notifications appear in the toaster of <UApp>. This component only
    exists to keep the watcher above running while it is mounted — which is what the v-if around it
    in AppIndex decides, and that condition has to stay reactive. Vue wants a root element, so this
    is an empty one; it is deliberately NOT the old hidden div, whose job was to trigger a computed.
  -->
  <span class="hidden" aria-hidden="true" />
</template>
